/**
 * GRAPHING annotation node types (FEATURE BUILD G1) — the single, dependency-free
 * source of truth for which canvas node types belong to the graphing /
 * instrumentation suite (trend graphs, tiles, gauges, cycle diagrams, …).
 *
 * These nodes are pure INSTRUMENTATION: they read solver state, they never
 * participate in it. Every solver-facing surface must treat them as invisible
 * (the same contract as `ANNOTATE-ONLY` types in `canvasComponentReadiness`):
 *
 *   - EO assemblers (`canvasToEoRequest`): guarded via {@link stripGraphingNodes}
 *     (tests in `__tests__/graphingSolverSafety.test.ts` prove the without-graphs
 *     request is byte-identical).
 *   - NS `networkBuilder` + `canvasComponentReadiness` registration: guard diffs
 *     live in `INTEGRATION_PATCHES.md` (those files are owned by other waves).
 *
 * Deliberately DEPENDENCY-FREE (no React, no store, no components) so solver
 * libs can import the predicate without dragging chart components into their
 * module graph.
 */

/** Canvas `node.type` strings owned by the graphing suite. */
export const GRAPHING_NODE_TYPES = [
  'trendGraph',
  'xyGraph',
  'equipmentCurve',
  'cycleDiagram',
  'displayTile',
  'gauge',
] as const;

export type GraphingNodeType = (typeof GRAPHING_NODE_TYPES)[number];

const GRAPHING_TYPE_SET: ReadonlySet<string> = new Set<string>(GRAPHING_NODE_TYPES);

/**
 * True when `type` is a graphing/instrumentation annotation node — a node that
 * must be INVISIBLE to every solver path (EO request assembly, EO closure
 * analysis, NS network build, BoP request assembly).
 */
export function isAnnotationNodeType(type: string | undefined | null): boolean {
  return type != null && GRAPHING_TYPE_SET.has(type);
}

/**
 * Strip graphing nodes (and, defensively, any edge touching one) from a
 * flowsheet-shaped object. Graphing nodes render no React Flow handles, so
 * edges onto them cannot normally exist — the edge filter is belt-and-braces
 * against programmatic edge injection.
 *
 * Identity-preserving fast path: when the flowsheet contains no graphing nodes
 * the SAME object is returned (no allocation), so solver inputs for canvases
 * without instrumentation are byte-identical to before this feature existed.
 */
export function stripGraphingNodes<
  N extends { id: string; type?: string },
  E extends { source: string; target: string },
  F extends { nodes: readonly N[]; edges: readonly E[] },
>(fs: F): F {
  const graphIds = new Set<string>();
  for (const n of fs.nodes) {
    if (isAnnotationNodeType(n.type)) graphIds.add(n.id);
  }
  if (graphIds.size === 0) return fs; // fast path — untouched canvases unchanged
  return {
    ...fs,
    nodes: fs.nodes.filter((n) => !graphIds.has(n.id)),
    edges: fs.edges.filter((e) => !graphIds.has(e.source) && !graphIds.has(e.target)),
  };
}
